import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, ChevronLeft, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrg } from '@/hooks/useCurrentOrg';

interface Course {
  id: string;
  name: string;
  description: string | null;
  grade: string | null;
  subject: string | null;
  is_published: boolean;
  org_id: string;
}

interface Lesson {
  id: string;
  title: string;
  description: string | null;
  position: number;
  is_published: boolean;
}

export default function OrgCourseEdit() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { canManage } = useCurrentOrg();
  const [course, setCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', description: '' });

  const load = async () => {
    if (!courseId) return;
    setLoading(true);
    const [{ data: c, error: cErr }, { data: ls, error: lErr }] = await Promise.all([
      supabase
        .from('courses')
        .select('id, name, description, grade, subject, is_published, org_id')
        .eq('id', courseId)
        .is('deleted_at', null)
        .maybeSingle(),
      supabase
        .from('course_lessons')
        .select('id, title, description, position, is_published')
        .eq('course_id', courseId)
        .is('deleted_at', null)
        .order('position', { ascending: true }),
    ]);
    if (cErr || !c) {
      toast.error('Course not found');
      navigate('/org/courses');
      return;
    }
    if (lErr) toast.error('Failed to load lessons');
    setCourse(c);
    setLessons(ls ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const togglePublish = async (next: boolean) => {
    if (!course) return;
    setCourse({ ...course, is_published: next });
    const { error } = await supabase
      .from('courses')
      .update({ is_published: next })
      .eq('id', course.id);
    if (error) {
      setCourse({ ...course, is_published: !next });
      toast.error(error.message);
    } else {
      toast.success(next ? 'Course published' : 'Course unpublished');
    }
  };

  const createLesson = async () => {
    if (!course) return;
    if (!form.title.trim()) {
      toast.error('Lesson title is required');
      return;
    }
    setCreating(true);
    const nextPosition = lessons.length;
    const { data, error } = await supabase
      .from('course_lessons')
      .insert({
        course_id: course.id,
        title: form.title.trim(),
        description: form.description.trim() || null,
        position: nextPosition,
      })
      .select('id')
      .single();
    setCreating(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setOpen(false);
    setForm({ title: '', description: '' });
    navigate(`/org/lessons/${data.id}`);
  };

  const move = async (lesson: Lesson, dir: -1 | 1) => {
    const idx = lessons.findIndex((l) => l.id === lesson.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= lessons.length) return;
    const other = lessons[swapIdx];
    const next = [...lessons];
    next[idx] = { ...other, position: lesson.position };
    next[swapIdx] = { ...lesson, position: other.position };
    setLessons(next.sort((a, b) => a.position - b.position));
    const { error } = await supabase.from('course_lessons').upsert([
      { id: lesson.id, position: other.position, course_id: course!.id, title: lesson.title },
      { id: other.id, position: lesson.position, course_id: course!.id, title: other.title },
    ]);
    if (error) {
      toast.error('Reorder failed');
      load();
    }
  };

  const softDelete = async (lesson: Lesson) => {
    if (!confirm(`Delete lesson "${lesson.title}"?`)) return;
    const { error } = await supabase
      .from('course_lessons')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', lesson.id);
    if (error) toast.error(error.message);
    else {
      toast.success('Lesson deleted');
      setLessons((ls) => ls.filter((l) => l.id !== lesson.id));
    }
  };

  if (loading || !course) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-4xl py-10">
      <Helmet>
        <title>{course.name} – Course editor</title>
        <meta name="description" content={`Edit lessons for ${course.name}.`} />
      </Helmet>

      <Link
        to="/org/courses"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ChevronLeft className="h-4 w-4 mr-1" /> All courses
      </Link>

      <header className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">{course.name}</h1>
          {course.description && (
            <p className="text-sm text-muted-foreground mt-1">{course.description}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {course.grade && <Badge variant="secondary">Grade {course.grade}</Badge>}
            {course.subject && <Badge variant="secondary">{course.subject}</Badge>}
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 shrink-0">
            <Label htmlFor="pub" className="text-sm">Published</Label>
            <Switch id="pub" checked={course.is_published} onCheckedChange={togglePublish} />
          </div>
        )}
      </header>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Lessons</CardTitle>
          {canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="mr-2 h-4 w-4" /> Add lesson
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>New lesson</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="l-title">Title</Label>
                    <Input
                      id="l-title"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="l-desc">Description</Label>
                    <Textarea
                      id="l-desc"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button onClick={createLesson} disabled={creating}>
                    {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent className="p-0 divide-y">
          {lessons.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No lessons yet.
            </div>
          ) : (
            lessons.map((l, i) => (
              <div key={l.id} className="flex items-center gap-3 p-4">
                <div className="text-xs text-muted-foreground w-6">{i + 1}</div>
                <Link to={`/org/lessons/${l.id}`} className="flex-1 min-w-0 hover:underline">
                  <div className="font-medium truncate">{l.title}</div>
                  {l.description && (
                    <div className="text-sm text-muted-foreground truncate">
                      {l.description}
                    </div>
                  )}
                </Link>
                <Badge variant={l.is_published ? 'default' : 'outline'} className="shrink-0">
                  {l.is_published ? 'Published' : 'Draft'}
                </Badge>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => move(l, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => move(l, 1)}
                      disabled={i === lessons.length - 1}
                      aria-label="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => softDelete(l)}
                      aria-label="Delete lesson"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
